import {
  getDefaultFilterOperatorsForType,
  type TableColumnDescriptor,
  type TableFieldType,
} from '@finance/contracts';

export type FilterOperator =
  | 'contains'
  | 'equals'
  | 'notEquals'
  | 'startsWith'
  | 'endsWith'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'between'
  | 'on'
  | 'before'
  | 'after'
  | 'isNull'
  | 'isNotNull'
  | 'in'
  | 'notIn';

export interface FilterOperatorDescriptor {
  id: FilterOperator;
  labelKey: string;
  applicableTypes: TableFieldType[];
  requiresValue: boolean;
  valueType: 'text' | 'number' | 'date' | 'datetime' | 'enum' | 'boolean' | 'range';
}

export const FILTER_OPERATORS: FilterOperatorDescriptor[] = [
  { id: 'contains', labelKey: 'filters.operators.contains', applicableTypes: ['text'], requiresValue: true, valueType: 'text' },
  { id: 'equals', labelKey: 'filters.operators.equals', applicableTypes: ['text', 'number', 'decimal', 'quantity', 'money', 'percentage', 'date', 'datetime', 'select', 'status', 'boolean', 'reference'], requiresValue: true, valueType: 'text' },
  { id: 'notEquals', labelKey: 'filters.operators.notEquals', applicableTypes: ['text', 'number', 'decimal', 'quantity', 'money', 'percentage', 'date', 'datetime', 'select', 'status', 'boolean', 'reference'], requiresValue: true, valueType: 'text' },
  { id: 'startsWith', labelKey: 'filters.operators.startsWith', applicableTypes: ['text'], requiresValue: true, valueType: 'text' },
  { id: 'endsWith', labelKey: 'filters.operators.endsWith', applicableTypes: ['text'], requiresValue: true, valueType: 'text' },
  { id: 'gt', labelKey: 'filters.operators.gt', applicableTypes: ['number', 'decimal', 'quantity', 'money', 'percentage'], requiresValue: true, valueType: 'number' },
  { id: 'gte', labelKey: 'filters.operators.gte', applicableTypes: ['number', 'decimal', 'quantity', 'money', 'percentage'], requiresValue: true, valueType: 'number' },
  { id: 'lt', labelKey: 'filters.operators.lt', applicableTypes: ['number', 'decimal', 'quantity', 'money', 'percentage'], requiresValue: true, valueType: 'number' },
  { id: 'lte', labelKey: 'filters.operators.lte', applicableTypes: ['number', 'decimal', 'quantity', 'money', 'percentage'], requiresValue: true, valueType: 'number' },
  { id: 'between', labelKey: 'filters.operators.between', applicableTypes: ['number', 'decimal', 'quantity', 'money', 'percentage', 'date', 'datetime'], requiresValue: true, valueType: 'range' },
  { id: 'on', labelKey: 'filters.operators.on', applicableTypes: ['date', 'datetime'], requiresValue: true, valueType: 'date' },
  { id: 'before', labelKey: 'filters.operators.before', applicableTypes: ['date', 'datetime'], requiresValue: true, valueType: 'date' },
  { id: 'after', labelKey: 'filters.operators.after', applicableTypes: ['date', 'datetime'], requiresValue: true, valueType: 'date' },
  { id: 'isNull', labelKey: 'filters.operators.isNull', applicableTypes: ['text', 'number', 'decimal', 'quantity', 'money', 'percentage', 'date', 'datetime', 'select', 'status', 'boolean', 'link', 'reference'], requiresValue: false, valueType: 'text' },
  { id: 'isNotNull', labelKey: 'filters.operators.isNotNull', applicableTypes: ['text', 'number', 'decimal', 'quantity', 'money', 'percentage', 'date', 'datetime', 'select', 'status', 'boolean', 'link', 'reference'], requiresValue: false, valueType: 'text' },
  { id: 'in', labelKey: 'filters.operators.in', applicableTypes: ['select', 'status', 'reference'], requiresValue: true, valueType: 'enum' },
  { id: 'notIn', labelKey: 'filters.operators.notIn', applicableTypes: ['select', 'status', 'reference'], requiresValue: true, valueType: 'enum' },
];

export function getOperatorsForType(fieldType: TableFieldType): FilterOperatorDescriptor[] {
  const allowed = new Set(getDefaultFilterOperatorsForType(fieldType));
  return FILTER_OPERATORS.filter(op => allowed.has(op.id));
}

export function getDefaultOperatorForType(fieldType: TableFieldType): FilterOperator {
  const operators = getOperatorsForType(fieldType);
  return operators[0]?.id ?? 'equals';
}

export interface ColumnFilterConfig {
  fieldType: TableFieldType;
  allowedOperators?: FilterOperator[];
  enumOptions?: { value: string; labelKey: string }[];
  referenceEntityType?: string;
  nullable?: boolean;
}

export interface FilterValue {
  operator: FilterOperator;
  value: unknown;
}

export interface FilterControlProps {
  column: TableColumnDescriptor;
  value: FilterValue | null;
  onChange: (value: FilterValue | null) => void;
  locale: string;
  disabled?: boolean;
}

type FilterControlFactory = (config: ColumnFilterConfig) => React.ComponentType<FilterControlProps>;

const filterControlRegistry = new Map<string, FilterControlFactory>();

export function registerFilterControl(type: string, factory: FilterControlFactory): void {
  filterControlRegistry.set(type, factory);
}

export function getFilterControl(type: string): FilterControlFactory | undefined {
  return filterControlRegistry.get(type);
}

export function resolveFilterType(column: TableColumnDescriptor): string {
  if (column.filterType) return column.filterType;
  return column.fieldType ?? 'text';
}

export function createFilterConfig(column: TableColumnDescriptor): ColumnFilterConfig {
  return {
    fieldType: column.fieldType ?? 'text',
    allowedOperators: column.allowedFilterOperators,
    enumOptions: column.enumOptions,
    referenceEntityType: column.referenceDescriptor?.entityType,
    nullable: column.nullable,
  };
}
